import type {
  AppointmentDocumentationSnapshotResponse,
  AppointmentManualGenerationErrorCode,
  AppointmentReEnhancementStateResponse,
} from "../../../shared/api-types";

/**
 * Transport: polling with reconnect. Events are advisory in the
 * AppointmentDocumentationPort contract — durable truth is the snapshot — so
 * the workspace re-reads `GET /documentation` on an interval and any missed
 * event is repaired by the next successful read. NO-772 adds a content-free
 * progress stream on top: it only marks "Writing" and asks for an early
 * re-read; the interval stays the floor.
 */
export const APPOINTMENT_DOCUMENTATION_POLL_INTERVAL_MS = 5000;

export type AppointmentDocumentationConnection = "connecting" | "live" | "reconnecting";

export type AppointmentEnhanceActionState =
  | "hidden"
  | "available"
  | "requesting"
  | "retry"
  | "unavailable";

/**
 * The generation surface, derived from the durable snapshot. `preparing` may
 * carry streamed draft text, but that text is advisory display only — it is
 * never editable or copyable, and a terminal `failed`/`cancelled` snapshot
 * discards it. Only `draft_ready`, which requires one persisted Draft
 * revision id, unlocks editing.
 */
export type AppointmentDraftGenerationUi =
  | { phase: "idle" }
  | {
      phase: "preparing";
      enhancementJobId: string | null;
      streamedDraftText: string;
      /** NO-772: the running generation's model output has begun. */
      writing: boolean;
    }
  | { phase: "failed"; enhancementJobId: string | null }
  | { phase: "cancelled"; enhancementJobId: string | null }
  | {
      phase: "draft_ready";
      enhancementJobId: string | null;
      draftRevisionId: string;
      /**
       * NO-772 scope change: the job's streamed text, kept locked on screen
       * while the persisted Draft loads. Present only when some streamed.
       */
      streamedDraftText?: string;
    };

/**
 * Advisory generation-progress signal from a push transport. Mirrors the
 * `generation_progress` event of the AppointmentDocumentationPort channel;
 * `textDelta` rides a separate authorized content stream and never any log,
 * analytics, or error path. Signals correlate to one technical generation
 * attempt: retries reuse the enhancementJobId but carry a fresh generationId,
 * and `sequence` is 1-based per attempt so replayed chunks are ignored and
 * gaps are detected.
 */
export interface AppointmentGenerationProgressSignal {
  enhancementJobId: string;
  generationId: string;
  /** NO-772: one generation run; a new run id starts a fresh preview. */
  runId?: string;
  sequence: number;
  textDelta?: string;
  final?: boolean;
}

/**
 * NO-772: one content-free progress signal from the documentation stream.
 * `writing` names the generation whose model output began; `settled` asks the
 * store to re-read durable state now instead of at the next interval;
 * `interrupted` means the workspace stopped following the attempt (the flag
 * turned off or the attempt left the snapshot), so "Writing" is dropped. A
 * mere connection drop is not an interruption: the replay restores the label.
 */
export type AppointmentEnhancementProgressSignal =
  | { type: "writing"; generationId: string }
  | { type: "settled" }
  | { type: "interrupted" };

export const STREAM_FLUSH_INTERVAL_MS = 75;
export const STREAM_FLUSH_BYTES = 1024;
/** Hard bound on the locked preview; anything longer waits for the durable Draft. */
export const MAX_STREAMED_PREVIEW_LENGTH = 262_144;

/**
 * NO-785: the latest RD Re-enhance, from the durable snapshot. `writing`
 * follows the same progress-stream rule as Enhance. Absent reads as no
 * Re-enhance and a Draft that was never approved.
 */
export interface AppointmentReEnhancementUi {
  state: AppointmentReEnhancementStateResponse | null;
  writing: boolean;
  draftEverApproved: boolean;
}

export interface AppointmentDocumentationUiSnapshot {
  connection: AppointmentDocumentationConnection;
  snapshot: AppointmentDocumentationSnapshotResponse | null;
  enhanceAction: AppointmentEnhanceActionState;
  generation: AppointmentDraftGenerationUi;
  reEnhancement?: AppointmentReEnhancementUi;
}

export type AppointmentEnhanceRequestOutcome =
  | {
      status: "claimed" | "already_active" | "already_complete";
      enhancementJobId: string;
    }
  | { status: "unavailable" };

export function appointmentDraftGenerationFromSnapshot(
  snapshot: AppointmentDocumentationSnapshotResponse | null,
  streamedDraftText = "",
  writing = false
): AppointmentDraftGenerationUi {
  if (!snapshot) return { phase: "idle" };

  const enhancementJobId = snapshot.enhancementJobId;

  switch (snapshot.enhancementState) {
    case "idle":
      return { phase: "idle" };
    case "failed":
      return { phase: "failed", enhancementJobId };
    case "cancelled":
      return { phase: "cancelled", enhancementJobId };
    case "draft_ready":
      return snapshot.draftRevisionId === null
        ? { phase: "preparing", enhancementJobId, streamedDraftText, writing }
        : {
            phase: "draft_ready",
            enhancementJobId,
            draftRevisionId: snapshot.draftRevisionId,
            ...(streamedDraftText ? { streamedDraftText } : {}),
          };
    case "claimed":
    case "generating":
      return { phase: "preparing", enhancementJobId, streamedDraftText, writing };
  }
}

/** NO-775: what the server answered a manual generation request. */
export type AppointmentManualGenerationRequestOutcome =
  | {
      status: "claimed" | "already_active" | "already_complete";
      enhancementJobId: string;
    }
  | { status: "refused"; code: AppointmentManualGenerationErrorCode };

/** NO-775: what the paste dialog shows after a submit. */
export type AppointmentManualGenerationResult =
  | { status: "started" }
  | { status: "refused"; code: AppointmentManualGenerationErrorCode };

export interface AppointmentManualGenerationInput {
  claimKey: string;
  transcript: string;
  priorNote?: string;
}

/** NO-785: what the server answered a Re-enhance request. */
export type AppointmentReEnhanceRequestOutcome =
  | { status: "claimed" | "already_active"; enhancementJobId: string }
  | { status: "draft_changed"; currentDraftRevisionId: string }
  | { status: "draft_approved" }
  | { status: "no_draft" }
  | { status: "unavailable" };

/** NO-785: what the Re-enhance dialog shows after a confirm. */
export type AppointmentReEnhanceResult =
  | { status: "started" }
  | { status: "draft_changed" }
  | { status: "draft_approved" }
  | { status: "failed" };

export interface AppointmentReEnhanceInput {
  claimKey: string;
  /** The Draft head the RD saw when the dialog opened. */
  draftRevisionId: string;
}

export type AppointmentDocumentationOperation =
  | "poll"
  | "enhance"
  | "manual_generation"
  | "re_enhance";

type EnhancePhase = "idle" | "requesting" | "failed" | "unavailable";

interface AppointmentDocumentationStateOptions {
  pollIntervalMs?: number;
  createClaimKey?: () => string;
  schedule?: (run: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
  onError?: (error: unknown, operation: AppointmentDocumentationOperation) => void;
  /** NO-775: posts a pasted Transcript; absent answers `manual_generation_unavailable`. */
  requestManualGeneration?: (
    input: AppointmentManualGenerationInput
  ) => Promise<AppointmentManualGenerationRequestOutcome>;
  /** NO-785: posts a Re-enhance; absent answers `failed`. */
  requestReEnhancement?: (
    input: AppointmentReEnhanceInput
  ) => Promise<AppointmentReEnhanceRequestOutcome>;
  /**
   * Optional push transport for generation progress. Signals are advisory:
   * they update the locked streamed preview, while the polled snapshot stays
   * the durable truth for every phase change.
   */
  subscribeToGenerationProgress?: (
    onSignal: (signal: AppointmentGenerationProgressSignal) => void
  ) => () => void;
}

interface GenerationStreamBuffer {
  enhancementJobId: string;
  generationId: string;
  runId: string | undefined;
  text: string;
  pending: string;
  lastSequence: number;
  gapped: boolean;
}

function generationHasSettled(
  snapshot: AppointmentDocumentationSnapshotResponse
): boolean {
  if (snapshot.enhancementState === "idle") return true;

  if (snapshot.enhancementState === "failed") return true;

  if (snapshot.enhancementState === "cancelled") return true;

  return (
    snapshot.enhancementState === "draft_ready" && snapshot.draftRevisionId !== null
  );
}

function refusalMayHaveSavedState(code: AppointmentManualGenerationErrorCode): boolean {
  return code === "transcript_exists" || code === "generation_not_started";
}

/** NO-785: a Re-enhance that is running, or failed or cancelled on the head it left. */
function isReEnhanceShowing(
  snapshot: AppointmentDocumentationSnapshotResponse
): boolean {
  const state = snapshot.reEnhancementState ?? null;

  return state !== null;
}

type StartedReEnhance = Extract<
  AppointmentReEnhanceRequestOutcome,
  { status: "claimed" | "already_active" }
>;

function isStartedReEnhance(
  outcome: AppointmentReEnhanceRequestOutcome
): outcome is StartedReEnhance {
  return outcome.status === "claimed" || outcome.status === "already_active";
}

function reEnhanceResultFor(
  outcome: Exclude<AppointmentReEnhanceRequestOutcome, StartedReEnhance>
): AppointmentReEnhanceResult {
  if (outcome.status === "draft_changed") return { status: "draft_changed" };

  if (outcome.status === "draft_approved") return { status: "draft_approved" };

  return { status: "failed" };
}

export class AppointmentDocumentationState {
  private snapshot: AppointmentDocumentationSnapshotResponse | null = null;
  private connection: AppointmentDocumentationConnection = "connecting";
  private enhancePhase: EnhancePhase = "idle";
  private streamBuffer: GenerationStreamBuffer | null = null;
  /** NO-772: the generation whose model output began, per the progress stream. */
  private writingGenerationId: string | null = null;
  /** NO-772: runs a newer run replaced; their late text is never shown again. */
  private readonly retiredRunIds = new Set<string>();
  /**
   * NO-785: generations the snapshot showed as a running Re-enhance. Their text
   * is adopted only while that generation is the running one, so a late line
   * can never rebuild a failed run's preview.
   */
  private readonly reEnhanceGenerationIds = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeGeneration: (() => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private polling = false;
  private pendingRefresh = false;
  private disposed = false;
  /** Increments when a read starts; refresh waiters resolve on the read they asked for. */
  private readSequence = 0;
  private refreshWaiters: Array<{ minimumSequence: number; resolve: () => void }> = [];
  private readonly pollIntervalMs: number;
  private readonly createClaimKey: () => string;
  private readonly schedule: NonNullable<
    AppointmentDocumentationStateOptions["schedule"]
  >;
  private readonly cancel: NonNullable<AppointmentDocumentationStateOptions["cancel"]>;
  private readonly onError: (
    error: unknown,
    operation: AppointmentDocumentationOperation
  ) => void;
  private readonly requestManualGeneration?: AppointmentDocumentationStateOptions["requestManualGeneration"];
  private readonly requestReEnhancement?: AppointmentDocumentationStateOptions["requestReEnhancement"];

  constructor(
    private readonly readSnapshot: () => Promise<AppointmentDocumentationSnapshotResponse>,
    private readonly requestEnhancement: (
      claimKey: string
    ) => Promise<AppointmentEnhanceRequestOutcome>,
    private readonly changed: (ui: AppointmentDocumentationUiSnapshot) => void,
    options: AppointmentDocumentationStateOptions = {}
  ) {
    this.pollIntervalMs =
      options.pollIntervalMs ?? APPOINTMENT_DOCUMENTATION_POLL_INTERVAL_MS;
    this.createClaimKey = options.createClaimKey ?? (() => crypto.randomUUID());
    const schedule = options.schedule;
    const cancel = options.cancel;
    this.schedule = schedule
      ? (run, delay) => schedule(run, delay)
      : (run, delay) => globalThis.setTimeout(run, delay);
    this.cancel = cancel
      ? (timer) => cancel(timer)
      : (timer) => globalThis.clearTimeout(timer);
    this.onError = options.onError ?? (() => {});
    this.requestManualGeneration = options.requestManualGeneration;
    this.requestReEnhancement = options.requestReEnhancement;
    this.subscribeToGenerationProgress = options.subscribeToGenerationProgress;
  }

  private readonly subscribeToGenerationProgress?: (
    onSignal: (signal: AppointmentGenerationProgressSignal) => void
  ) => () => void;

  start(): void {
    if (this.disposed) return;

    if (this.subscribeToGenerationProgress && !this.unsubscribeGeneration) {
      this.unsubscribeGeneration = this.subscribeToGenerationProgress((signal) =>
        this.onGenerationSignal(signal)
      );
    }

    void this.poll();
  }

  /**
   * Immediate re-read of the durable snapshot — the "Check again" action, and
   * what approval calls so the writeback state lands without waiting for the
   * interval. Resolves once a read that STARTED at or after this call has
   * SUCCEEDED, so a caller can tell the answer reflects its own action rather
   * than a read already in flight. A failed read keeps the promise pending:
   * the store is reconnecting and its snapshot is the pre-call one, which is
   * not an answer; the next successful poll settles it. Dispose settles every
   * waiter so an unmounted surface never hangs.
   */
  refresh(): Promise<void> {
    if (this.disposed) return Promise.resolve();

    const settled = new Promise<void>((resolve) => {
      this.refreshWaiters.push({ minimumSequence: this.readSequence + 1, resolve });
    });

    if (this.timer) {
      this.cancel(this.timer);
      this.timer = null;
    }

    if (this.polling) {
      // The in-flight read started before this refresh, so its response may
      // predate whatever prompted the refresh. Queue one follow-up read to
      // run as soon as the current poll settles.
      this.pendingRefresh = true;

      return settled;
    }

    void this.poll();

    return settled;
  }

  enhance(): void {
    if (this.disposed) return;

    const action = this.enhanceActionState();
    if (action !== "available" && action !== "retry") return;

    this.enhancePhase = "requesting";
    this.emit();
    void this.runEnhance();
  }

  /**
   * NO-775: generates the draft from a pasted Transcript. A started request
   * takes the same optimistic `claimed` step Enhance does, then re-reads the
   * durable snapshot. A refusal is returned for the dialog to explain; one
   * that may have changed durable state (a Transcript now exists, or it was
   * saved but did not start) re-reads at once.
   */
  async generateFromPastedTranscript(
    input: AppointmentManualGenerationInput
  ): Promise<AppointmentManualGenerationResult> {
    if (this.disposed || !this.requestManualGeneration) {
      return { status: "refused", code: "manual_generation_unavailable" };
    }

    let outcome: AppointmentManualGenerationRequestOutcome;

    try {
      outcome = await this.requestManualGeneration(input);
    } catch (error) {
      if (!this.disposed) this.onError(error, "manual_generation");

      return { status: "refused", code: "generation_not_started" };
    }

    if (this.disposed) return { status: "started" };

    if (outcome.status === "refused") {
      if (refusalMayHaveSavedState(outcome.code)) void this.refresh();

      return { status: "refused", code: outcome.code };
    }

    this.applyOptimisticClaim(outcome);
    this.emit();
    void this.refresh();

    return { status: "started" };
  }

  /**
   * NO-785: Re-enhance the Draft head the RD saw. A started run marks the
   * snapshot running at once and re-reads durable state; a refusal that means
   * the Draft moved or was approved re-reads too, so the workspace catches up.
   */
  async reEnhance(
    input: AppointmentReEnhanceInput
  ): Promise<AppointmentReEnhanceResult> {
    if (this.disposed || !this.requestReEnhancement) return { status: "failed" };

    let outcome: AppointmentReEnhanceRequestOutcome;

    try {
      outcome = await this.requestReEnhancement(input);
    } catch (error) {
      if (!this.disposed) this.onError(error, "re_enhance");

      return { status: "failed" };
    }

    if (this.disposed) return { status: "started" };

    if (!isStartedReEnhance(outcome)) {
      void this.refresh();

      return reEnhanceResultFor(outcome);
    }

    this.applyOptimisticReEnhance(outcome.enhancementJobId);
    this.emit();
    void this.refresh();

    return { status: "started" };
  }

  /**
   * NO-772: applies one progress-stream signal. `writing` counts only for the
   * generation the durable snapshot names; `settled` re-reads durable state
   * at once; `interrupted` drops "Writing". None changes a phase on its own.
   */
  applyEnhancementProgress(signal: AppointmentEnhancementProgressSignal): void {
    if (this.disposed) return;

    if (signal.type === "settled") {
      void this.refresh();

      return;
    }

    if (signal.type === "interrupted") {
      this.clearWriting();

      return;
    }

    const generationId = this.snapshot?.generationId ?? null;
    if (generationId === null || signal.generationId !== generationId) return;

    if (this.writingGenerationId === generationId) return;

    this.writingGenerationId = generationId;
    this.emit();
  }

  uiSnapshot(): AppointmentDocumentationUiSnapshot {
    return {
      connection: this.connection,
      snapshot: this.snapshot,
      enhanceAction: this.enhanceActionState(),
      generation: this.generationUi(),
      reEnhancement: this.reEnhancementUi(),
    };
  }

  dispose(): void {
    this.disposed = true;
    this.resolveRefreshWaiters(Number.POSITIVE_INFINITY);
    if (this.timer) this.cancel(this.timer);

    this.timer = null;
    if (this.flushTimer) this.cancel(this.flushTimer);

    this.flushTimer = null;

    if (this.unsubscribeGeneration) {
      this.unsubscribeGeneration();
      this.unsubscribeGeneration = null;
    }
  }

  private async poll(): Promise<void> {
    if (this.disposed || this.polling) return;

    this.polling = true;
    const sequence = ++this.readSequence;

    try {
      const snapshot = await this.readSnapshot();
      if (this.disposed) return;

      this.snapshot = snapshot;
      this.connection = "live";
      this.rememberReEnhanceGeneration();
      this.reconcileStreamBuffer();
      this.reconcileWriting();
      this.resolveRefreshWaiters(sequence);
    } catch (error) {
      if (this.disposed) return;

      this.onError(error, "poll");
      this.connection = "reconnecting";
    } finally {
      this.polling = false;
    }

    this.emit();

    if (this.pendingRefresh) {
      // A refresh arrived while this read was in flight; its response may be
      // stale, so run the queued durable re-read now instead of waiting for
      // the interval.
      this.pendingRefresh = false;
      void this.poll();

      return;
    }

    this.scheduleNextPoll();
  }

  /** Settles every refresh whose requested read has now succeeded. */
  private resolveRefreshWaiters(settledSequence: number): void {
    const remaining: typeof this.refreshWaiters = [];

    for (const waiter of this.refreshWaiters) {
      if (waiter.minimumSequence <= settledSequence) waiter.resolve();
      else remaining.push(waiter);
    }

    this.refreshWaiters = remaining;
  }

  private scheduleNextPoll(): void {
    if (this.disposed || this.timer) return;

    this.timer = this.schedule(() => {
      this.timer = null;
      void this.poll();
    }, this.pollIntervalMs);
  }

  private async runEnhance(): Promise<void> {
    let outcome: AppointmentEnhanceRequestOutcome;

    try {
      outcome = await this.requestEnhancement(this.createClaimKey());
    } catch (error) {
      if (this.disposed) return;

      this.onError(error, "enhance");
      this.enhancePhase = "failed";
      this.emit();

      return;
    }

    if (this.disposed) return;

    if (outcome.status === "unavailable") {
      this.enhancePhase = "unavailable";
      this.emit();

      return;
    }

    this.enhancePhase = "idle";
    this.applyOptimisticClaim(outcome);
    this.emit();
    this.refresh();
  }

  private applyOptimisticClaim(outcome: {
    status: "claimed" | "already_active" | "already_complete";
    enhancementJobId: string;
  }): void {
    if (!this.snapshot) return;

    this.snapshot = {
      ...this.snapshot,
      transcriptState: "ready",
      enhancementState:
        outcome.status === "already_complete" ? "draft_ready" : "claimed",
      enhancementJobId: outcome.enhancementJobId,
    };
  }

  /** NO-785: the store shows the run as running until the next read lands. */
  private applyOptimisticReEnhance(enhancementJobId: string): void {
    if (!this.snapshot) return;

    this.snapshot = {
      ...this.snapshot,
      enhancementJobId,
      generationId: null,
      reEnhancementState: "running",
    };
  }

  private reEnhancementUi(): AppointmentReEnhancementUi {
    const snapshot = this.snapshot;
    const state = snapshot?.reEnhancementState ?? null;
    const generationId = snapshot?.generationId ?? null;
    const writing = [
      state === "running",
      generationId !== null,
      this.writingGenerationId === generationId,
    ].every(Boolean);

    return { state, writing, draftEverApproved: snapshot?.draftEverApproved === true };
  }

  private rememberReEnhanceGeneration(): void {
    const snapshot = this.snapshot;
    if (snapshot?.reEnhancementState !== "running" || !snapshot.generationId) return;

    this.reEnhanceGenerationIds.add(snapshot.generationId);
  }

  private enhanceActionState(): AppointmentEnhanceActionState {
    if (this.enhancePhase === "unavailable") return "unavailable";

    if (this.enhancePhase === "requesting") return "requesting";

    const snapshot = this.snapshot;
    if (!snapshot || snapshot.transcriptState !== "ready") return "hidden";

    if (this.enhancePhase === "failed") return "retry";

    const isEnhanceable =
      snapshot.enhancementState === "idle" ||
      snapshot.enhancementState === "failed" ||
      snapshot.enhancementState === "cancelled";

    return isEnhanceable ? "available" : "hidden";
  }

  private generationUi(): AppointmentDraftGenerationUi {
    const snapshot = this.snapshot;
    const streamedDraftText = snapshot ? this.streamedTextFor(snapshot) : "";

    const generationId = snapshot?.generationId ?? null;
    const writing = generationId !== null && this.writingGenerationId === generationId;

    return appointmentDraftGenerationFromSnapshot(snapshot, streamedDraftText, writing);
  }

  private streamedTextFor(snapshot: AppointmentDocumentationSnapshotResponse): string {
    const buffer = this.streamBuffer;
    if (!buffer || buffer.gapped) return "";

    // NO-785: Re-enhance output never becomes the Draft tab's locked preview;
    // the tab shows the head read-only instead.
    if (isReEnhanceShowing(snapshot)) return "";

    // NO-772: a landed Draft keeps its own job's preview until it opens.
    const sameAttempt =
      snapshot.enhancementState === "draft_ready"
        ? buffer.enhancementJobId === snapshot.enhancementJobId
        : buffer.generationId === snapshot.generationId;

    return sameAttempt ? buffer.text : "";
  }

  /**
   * NO-772 scope change: one draft-text line from the clinical-read stream,
   * fed through the same locked-preview buffer as any generation signal.
   */
  applyGenerationText(line: {
    generationId: string;
    runId: string;
    sequence: number;
    textDelta: string;
  }): void {
    const enhancementJobId = this.snapshot?.enhancementJobId;
    if (!enhancementJobId) return;

    this.onGenerationSignal({ enhancementJobId, ...line });
  }

  private onGenerationSignal(signal: AppointmentGenerationProgressSignal): void {
    if (this.disposed) return;

    // Adopt-or-ignore, correlated with the durable snapshot: once the
    // snapshot identifies the active generation, only its signals may touch
    // the buffer — a late signal from a superseded attempt must never evict
    // the active attempt's accumulated preview or reset its sequence.
    const buffer = this.bufferForSignal(signal);
    if (!buffer) return;

    if (signal.sequence <= buffer.lastSequence) return;

    if (signal.sequence > buffer.lastSequence + 1) {
      this.discardGappedPreview(buffer, signal.sequence);
      if (signal.final) this.refresh();

      return;
    }

    buffer.lastSequence = signal.sequence;
    this.appendStreamDelta(buffer, signal.textDelta);

    if (signal.final) {
      this.flushStream();
      this.refresh();

      return;
    }

    if (buffer.pending.length >= STREAM_FLUSH_BYTES) {
      this.flushStream();

      return;
    }

    this.scheduleStreamFlush();
  }

  private bufferForSignal(
    signal: AppointmentGenerationProgressSignal
  ): GenerationStreamBuffer | null {
    if (this.isStrayReEnhanceSignal(signal)) return null;

    const snapshotGenerationId = this.snapshot?.generationId ?? null;

    if (snapshotGenerationId && signal.generationId !== snapshotGenerationId) {
      return null;
    }

    const current = this.streamBuffer;
    if (signal.runId && this.retiredRunIds.has(signal.runId)) return null;

    if (current?.generationId === signal.generationId) {
      // NO-772: a redelivered run restarts the text; never splice two runs.
      if (current.runId === signal.runId) return current;

      if (signal.generationId !== snapshotGenerationId) return null;

      if (current.runId) this.retiredRunIds.add(current.runId);
    } else if (current && signal.generationId !== snapshotGenerationId) {
      // Before the snapshot identifies a generation, an arbitrary different
      // signal cannot replace the attempt whose preview is already buffered.
      return null;
    }

    const buffer: GenerationStreamBuffer = {
      enhancementJobId: signal.enhancementJobId,
      generationId: signal.generationId,
      runId: signal.runId,
      text: "",
      pending: "",
      lastSequence: 0,
      gapped: false,
    };
    this.streamBuffer = buffer;

    return buffer;
  }

  /**
   * NO-785: a Re-enhance generation's text counts only while the snapshot shows
   * that generation running. First-Enhance signals are never touched here.
   */
  private isStrayReEnhanceSignal(signal: AppointmentGenerationProgressSignal): boolean {
    if (!this.reEnhanceGenerationIds.has(signal.generationId)) return false;

    const snapshot = this.snapshot;
    const isRunning =
      snapshot?.reEnhancementState === "running" &&
      snapshot.generationId === signal.generationId;

    return !isRunning;
  }

  private discardGappedPreview(buffer: GenerationStreamBuffer, sequence: number): void {
    // A gap means the preview would silently misrepresent the Draft;
    // discard it and let the durable snapshot resolve the surface.
    buffer.gapped = true;
    buffer.text = "";
    buffer.pending = "";
    buffer.lastSequence = sequence;
    this.flushStream();
  }

  private appendStreamDelta(
    buffer: GenerationStreamBuffer,
    textDelta: string | undefined
  ): void {
    if (!textDelta || buffer.gapped) return;

    const remaining =
      MAX_STREAMED_PREVIEW_LENGTH - buffer.text.length - buffer.pending.length;
    if (remaining > 0) buffer.pending += textDelta.slice(0, remaining);
  }

  private scheduleStreamFlush(): void {
    if (this.flushTimer) return;

    this.flushTimer = this.schedule(() => {
      this.flushTimer = null;
      this.flushStream();
    }, STREAM_FLUSH_INTERVAL_MS);
  }

  /** Moves buffered deltas into the rendered preview — at most one emit per flush. */
  private flushStream(): void {
    if (this.flushTimer) {
      this.cancel(this.flushTimer);
      this.flushTimer = null;
    }

    const buffer = this.streamBuffer;

    if (buffer && buffer.pending) {
      buffer.text += buffer.pending;
      buffer.pending = "";
    }

    this.emit();
  }

  private clearWriting(): void {
    if (this.writingGenerationId === null) return;

    this.writingGenerationId = null;
    this.emit();
  }

  /** "Writing" belongs to one generation; any other durable answer clears it. */
  private reconcileWriting(): void {
    if (this.snapshot?.generationId !== this.writingGenerationId) {
      this.writingGenerationId = null;
    }
  }

  /** Failed or cancelled partial output is discarded, never shown again. */
  private reconcileStreamBuffer(): void {
    const snapshot = this.snapshot;
    if (!snapshot || !this.streamBuffer) return;

    // NO-772: a landed Draft keeps its preview until the editor opens it.
    const generationSettled =
      generationHasSettled(snapshot) && snapshot.enhancementState !== "draft_ready";
    // A buffer for a generation the snapshot no longer names belongs to a
    // superseded attempt; drop it so the durable truth stays authoritative.
    const generationSuperseded =
      snapshot.generationId !== null &&
      this.streamBuffer.generationId !== snapshot.generationId;
    // NO-785: a Re-enhance buffer lives only while its run is the running one.
    const reEnhanceEnded =
      this.reEnhanceGenerationIds.has(this.streamBuffer.generationId) &&
      snapshot.reEnhancementState !== "running";

    if (generationSettled || generationSuperseded || reEnhanceEnded) {
      this.streamBuffer = null;

      if (this.flushTimer) {
        this.cancel(this.flushTimer);
        this.flushTimer = null;
      }
    }
  }

  private emit(): void {
    if (this.disposed) return;

    this.changed(this.uiSnapshot());
  }
}
